import React, { useState, useEffect } from "react";
import UserService from "../services/UserService";
import { getCompanies } from "../services/CompanyService";
import { FaUser, FaEnvelope } from "react-icons/fa";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import CreateNewUserModal from "./modals/CreateNewUserModal";

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState("");
  const [tempSearch, setTempSearch] = useState("");
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [editingMatriculaUserId, setEditingMatriculaUserId] = useState(null);
  const [matriculaValue, setMatriculaValue] = useState("");
  const [companies, setCompanies] = useState([]);

  // Carrega a lista de empresas uma única vez para popular o dropdown da coluna Empresa
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await getCompanies();
        if (data && data.companies) {
          setCompanies(data.companies);
        }
      } catch (error) {
        console.error("Erro ao buscar empresas:", error);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await UserService.getUsers(search, currentPage, 10);
        if (data) {
          setUsers(data.users);
          setTotalPages(data.totalPages);
          setTotalUsers(data.totalUsers);
        }
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
      }
    };

    fetchUsers();
  }, [search, currentPage]);

  const handleSearchChange = (e) => {
    setTempSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchBlur = () => {
    setSearch(tempSearch);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const clearFilters = () => {
    setTempSearch('')
    setSearch('');
  }

  const handleMassiveImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (!jsonData.length) {
        toast.error("O arquivo está vazio!");
        return;
      }

      const usersToImport = jsonData.map((row) => ({
        name: row["Nome"] || "",
        email: row["e-mail"] || "",
        phone: row["Telefone"] || "",
        reLegal: row["RE / Legal"] || "",
        uf: row["Estado"] || "",
        city: row["Cidade"] || "",
        role: row["Perfil"] ? row["Perfil"].toLowerCase() : "", // Convertendo para lowercase
        company: row["Empresa"] || "",
        password: "@merinode2025", // Definir senha padrão
        confirmpassword: "@merinode2025",
      }));

      // Validação básica antes de enviar os dados
      const invalidUsers = usersToImport.filter(
        (user) => !user.name || !user.email || !user.phone || !user.uf || !user.role || !user.company
      );

      if (invalidUsers.length > 0) {
        toast.error(`Erro: Alguns usuários possuem campos obrigatórios em branco.`);
        return;
      }

      // Enviar os usuários para o backend
      try {
        const results = await Promise.all(usersToImport.map(UserService.createUser));
        const successCount = results.filter((res) => res !== null).length;

        toast.success(`${successCount} usuários importados com sucesso!`);
        setCurrentPage(1); // Atualizar lista após importação
      } catch (error) {
        toast.error("Erro ao importar usuários.");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleResetPassword = async (userId) => {
    const confirmReset = window.confirm("Deseja realmente resetar a senha deste usuário?");
    if (confirmReset) {
      try {
        await UserService.resetPassword(userId);
        toast.success("Senha redefinida com sucesso!");
      } catch (error) {
        toast.error("Erro ao redefinir senha: " + error.message);
      }
    }
  };

  const handleStartMatriculaEdit = (user) => {
    setEditingMatriculaUserId(user._id);
    setMatriculaValue(user.reLegal || "");
  };

  const handleCancelMatriculaEdit = () => {
    setEditingMatriculaUserId(null);
    setMatriculaValue("");
  };

  const isValidMatricula = (value) => !value || /^80\d{6}$/.test(value);

  const handleMatriculaChange = async (userId) => {
    if (!isValidMatricula(matriculaValue)) {
      toast.error("A matrícula deve estar vazia ou seguir o padrão 80XXXXXX.");
      return;
    }

    try {
      await UserService.updateUser(userId, { reLegal: matriculaValue });
      toast.success("Matrícula atualizada com sucesso!");
      setUsers(users.map(user => user._id === userId ? { ...user, reLegal: matriculaValue } : user));
      handleCancelMatriculaEdit();
    } catch (error) {
      toast.error("Erro ao atualizar matrícula do usuário: " + error.message);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await UserService.updateUser(userId, { role: newRole });
      toast.success("Papel do usuário atualizado com sucesso!");
      setUsers(users.map(user => user._id === userId ? { ...user, role: newRole } : user));
    } catch (error) {
      toast.error("Erro ao atualizar papel do usuário: " + error.message);
    }
  };

  const handleCompanyChange = async (userId, newCompanyId) => {
    try {
      await UserService.updateUser(userId, { company: newCompanyId });
      toast.success("Empresa do usuário atualizada com sucesso!");
      // Mantém o `company` localmente no formato populado ({ _id, name, ... })
      // para que a coluna continue exibindo o nome corretamente.
      const newCompany = companies.find((c) => c._id === newCompanyId) || null;
      setUsers(users.map(user => user._id === userId ? { ...user, company: newCompany } : user));
    } catch (error) {
      toast.error("Erro ao atualizar empresa do usuário: " + error.message);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await UserService.updateUser(userId, { status: newStatus });
      toast.success("Status do usuário atualizado com sucesso!");
      setUsers(users.map(user => user._id === userId ? { ...user, status: newStatus } : user));
    } catch (error) {
      toast.error("Erro ao atualizar status do usuário: " + error.message);
    }
  };

  return (
    <div className="flex w-full h-full justify-center items-center py-4 px-4 bg-gray-200">
      <div className="flex flex-col w-full h-full rounded-xl py-8 px-6 gap-12 bg-white">
        <div className="flex flex-col w-full h-full gap-2 border border-gray-800 border-opacity-40 rounded-xl p-2">
          <div className="flex items-center gap-2 w-full text-sm font-semibold ps-2 mt-2">

            <div className="flex gap-2 w-full text-md font-semibold ps-2 mt-2">
              <FaUser size={20} className="text-gray-600" />
              Administração de Usuários
            </div>

            {/* <button type="file" onChange={handleMassiveImport} className="flex justify-center items-center bg-green-600 px-2 py-1 rounded-lg text-white gap-2">
              <FaFileExcel />
              <div>Importar</div>
            </button> */}

          <input type="file" accept=".xls,.xlsx" onChange={handleMassiveImport} />
            
          </div>

          <div className="flex items-end w-full rounded-md gap-4 mt-2 text-[10px]">
            <div className="flex space-x-4 text-[12px]">
              <div className="flex flex-col">
                <label className="font-semibold ms-2">Pesquisar Nome</label>
                <input
                  type="text"
                  placeholder="Digite o nome"
                  value={tempSearch}
                  onChange={handleSearchChange}
                  onBlur={handleSearchBlur}
                  className="block w-full h-5 rounded-2xl px-4 text-gray-900 shadow-sm placeholder:text-gray-400 border border-zinc-500 outline-none ring-0"
                />
              </div>
            </div>

            <button onClick={clearFilters} className="bg-zinc-800 hover:bg-zinc-700 text-white h-5 px-4 rounded-2xl w-40">
              Limpar Filtros
            </button>

            <button
              onClick={() => setShowCreateUserModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white h-5 px-4 rounded-2xl w-40"
            >
              +Novo Usuário
            </button>

          </div>


          <div className="overflow-x-auto mt-4">
          <div className="flex gap-1 ms-2 text-xs">Total: <div className="font-bold">{totalUsers} registros</div></div>
            <table className="min-w-full bg-white rounded-lg text-[10px]">
              <thead className="bg-gray-500 text-white">
                <tr>
                  <th className="py-2 px-4 border-b">Nome</th>
                  <th className="py-2 px-4 border-b">Matrícula</th>
                  <th className="py-2 px-4 border-b">Status</th>
                  <th className="py-2 px-4 border-b">Telefone</th>
                  <th className="py-2 px-4 border-b">Empresa</th>
                  <th className="py-2 px-4 border-b">Papel</th>
                  <th className="py-2 px-4 border-b">Último Login</th>
                  <th className="py-2 px-4 border-b">Ação</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={index} className="border-b hover:bg-gray-100">
                    <td className="py-1 px-4 flex items-center gap-2">
                      <FaUser className="text-blue-600" />
                      {user.name}
                    </td>
                    <td className="py-1 px-4 flex items-center gap-2">
                      <FaEnvelope className="text-gray-600" />
                      {user.email}
                    </td>
                    <td className="py-1 px-4">
                      {editingMatriculaUserId === user._id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={matriculaValue}
                            onChange={(e) => setMatriculaValue(e.target.value)}
                            maxLength={8}
                            placeholder="80XXXXXX"
                            className={`block w-24 rounded-2xl px-2 py-1 text-gray-900 shadow-sm placeholder:text-gray-400 border outline-none ring-0 ${isValidMatricula(matriculaValue) ? "border-zinc-500" : "border-red-500"}`}
                          />
                          <button
                            onClick={() => handleMatriculaChange(user._id)}
                            className="bg-green-600 hover:bg-green-700 text-white px-2 rounded-2xl"
                          >
                            Salvar
                          </button>
                          <button
                            onClick={handleCancelMatriculaEdit}
                            className="bg-zinc-500 hover:bg-zinc-600 text-white px-2 rounded-2xl"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>{user.reLegal || "-"}</span>
                          <button
                            onClick={() => handleStartMatriculaEdit(user)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-0 rounded-2xl"
                          >
                            Alterar
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="py-1 px-4">
                      <select
                        value={user.status}
                        onChange={(e) => handleStatusChange(user._id, e.target.value)}
                        className="block w-full rounded-2xl px-2 py-1 text-gray-900 shadow-sm placeholder:text-gray-400 border border-zinc-500 outline-none ring-0"
                      >
                        <option value="0">Inativo</option>
                        <option value="1">Ativo</option>
                      </select>
                    </td>
                    <td className="py-1 px-4">{user.phone}</td>
                    <td className="py-1 px-4">
                      <select
                        value={user.company?._id || ""}
                        onChange={(e) => handleCompanyChange(user._id, e.target.value)}
                        className="block w-full rounded-2xl px-2 py-1 text-gray-900 shadow-sm placeholder:text-gray-400 border border-zinc-500 outline-none ring-0"
                      >
                        <option value="" disabled>-</option>
                        {companies.map((company) => (
                          <option key={company._id} value={company._id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1 px-4">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user._id, e.target.value)}
                        className="block w-full rounded-2xl px-2 py-1 text-gray-900 shadow-sm placeholder:text-gray-400 border border-zinc-500 outline-none ring-0"
                      >
                        <option value="admin">admin</option>
                        <option value="user">user</option>
                        <option value="tecnico">tecnico</option>
                        <option value="warehouse">warehouse</option>
                        <option value="vivogestao">vivogestao</option>
                        <option value="report-reader">report-reader</option>
                        <option value="entregador">entregador</option>
                      </select>
                    </td>
                    <td className="py-1 px-4">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : "Nunca logado"}
                    </td>
                    <td className="py-1 px-4">
                      <div className="flex justify-center gap-2 h-5 text-[11px]">
                        <button
                          onClick={() => handleResetPassword(user._id)}
                          className="flex items-center gap-1 bg-yellow-500 rounded-full px-2 text-white"
                        >
                          Redefinir Senha
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          {/* Pagination Controls */}
          <div className="flex justify-center items-center mt-4 gap-2 text-xs">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 text-white'}`}
              >
                {'<'}
              </button>

              {/* Botão para a primeira página */}
              {currentPage > 2 && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="px-2 py-1 rounded-md bg-gray-300"
                  >
                    1
                  </button>
                  {currentPage >= 3 && <span className="px-2 py-1">...</span>}
                </>
              )}

              {/* Botões ao redor da página atual */}
              {Array.from({ length: 3 }, (_, i) => {
                const page = currentPage - 1 + i;
                if (page > 0 && page <= totalPages) {
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2 py-1 rounded-md ${page === currentPage ? 'bg-blue-500 text-white' : 'bg-gray-300'}`}
                    >
                      {page}
                    </button>
                  );
                }
                return null;
              })}

              {/* Botão para a última página */}
              {currentPage < totalPages - 1 && (
                <>
                  {currentPage < totalPages - 2 && <span className="px-2 py-1">...</span>}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-2 py-1 rounded-md bg-gray-300"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-2 py-1 rounded-md ${currentPage === totalPages ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 text-white'}`}
              >
                {'>'}
              </button>
            </div>

        </div>
      </div>

      <CreateNewUserModal
        isOpen={showCreateUserModal}
        setIsOpen={setShowCreateUserModal}
        onUserCreated={() => setCurrentPage(1)} // Atualiza a lista após cadastro
      />

    </div>
  );
}
